<template>
  <div class="block mx-auto">
    <div class="bg-grey-background-subdued sm:bg-white sm:box-shadow-sm p-16">
      <p class="text-overline text-grey-header">
        {{ isAdditionalProfile ? 'Profile added!' : "You're in!" }}
      </p>
      <div class="flex gap-16 mt-16">
        <ProgressBar :value="1" :max="1" :content="isAdditionalProfile ? 'Profile added!' : 'You\'re in!'" />
        <ProgressBar :value="1" :max="1" content="" />
      </div>
    </div>
    <div class="mx-auto lg:max-w-[986px]">
      <h1 class="text-h1 mt-24 mb-24 lg:mb-48 px-16">
        {{ isAdditionalProfile ? `You're ready to find new opportunities` : "You're ready to start applying to jobs" }}
      </h1>
      <div class="flex flex-col-reverse gap-[24px] lg:flex-row lg:gap-[48px]">
        <ProfileCompleteActions
          :is-additional-profile="isAdditionalProfile"
          :has-photo="hasPhoto"
          :photo-profile-type="photoProfileType"
          :formatted-care-type="formattedCareType"
          :job-search-link="jobSearchLink"
          :profile-edit-link="profileEditLink"
        />
        <ProfileCompletePreview :account="account" :profile-preview="profilePreview" :photo-profile-type="photoProfileType" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onBeforeMount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import ProfileCompleteActions from './ProfileCompleteActions.vue';
import ProfileCompletePreview from './ProfileCompletePreview.vue';
import { sendSegmentEvent } from '@/lib/segment';
import { readDomainCookie, deleteDomainCookie } from '@/lib/domain_cookie';
import { fetchProfileCompletePreview, type ProfileCompletePreviewData } from '@/lib/sitterOnboarding/profileCompletePreview';
import { CareType } from '@/types/care_type';
import { getRouteQueryParam } from '@/helpers/routeQuery';

defineOptions({ name: 'ProfileComplete' });

const store = useStore();
const instance = getCurrentInstance();
const profilePreview = ref<ProfileCompletePreviewData | null>(null);
const routeQuery = computed(() => instance?.proxy?.$route?.query);

const account = computed(() => store.getters['account/getAccount']);
const isAdditionalProfile = computed(
  () => getRouteQueryParam(routeQuery.value, 'additionalProfile') === 'true' || readDomainCookie('additional_profile') === 'true'
);
const careType = computed(() => resolveCareType() || 'babysitter');
const sanitizedCareType = computed(() => snakeCase(careType.value) || 'babysitter');
const photoProfileType = computed(() => {
  const value = sanitizedCareType.value.toUpperCase() as CareType;
  return Object.values(CareType).includes(value) ? value : CareType.BABYSITTER;
});
const formattedCareType = computed(() => sanitizedCareType.value.split('_').join(' '));
const profileEditLink = computed(() => `/provider/profile/${sanitizedCareType.value}/${account.value?.id}`);
const jobSearchLink = computed(() => `/provider/jobs/search?careType=${sanitizedCareType.value}&zipcode=${zipCode.value}`);
const hasPhoto = computed(() => !!account.value?.croppedPhoto);
const zipCode = computed(() => account.value?.location?.zipcode || '');

onBeforeMount(() => {
  void loadProfilePreview();
});

onMounted(() => {
  blurAllCards();
  if (getRouteQueryParam(routeQuery.value, 'profileCompleted') === 'true' || readDomainCookie('profile_completed')) {
    trackProfileCompleted();
  }
});

function resolveCareType(): string {
  const fromQuery = getRouteQueryParam(routeQuery.value, 'careType');
  if (fromQuery) return camelCase(fromQuery);

  const fromStore = store.getters.careType;
  if (fromStore) return fromStore;

  if (account.value?.careType) return camelCase(account.value.careType);

  return '';
}

async function loadProfilePreview(): Promise<void> {
  const resolvedCareType = resolveCareType();
  const accountId = account.value?.id;
  if (!accountId || !resolvedCareType) return;

  try {
    profilePreview.value = await fetchProfileCompletePreview(instance?.proxy?.$apollo, accountId, resolvedCareType);
  } catch (error) {
    console.error('Failed to fetch profile preview on profile complete:', error);
  }
}

function trackProfileCompleted(): void {
  deleteDomainCookie('profile_completed');
  if (isAdditionalProfile.value) deleteDomainCookie('additional_profile');

  sendSegmentEvent(isAdditionalProfile.value ? 'Sitter Additional Profile Completed' : 'Sitter Profile Completed', {
    profile_type: careType.value,
  });
}

function blurAllCards(): void {
  nextTick(() => {
    if (document.activeElement) (document.activeElement as HTMLElement).blur();
  });
}
</script>
